import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/*
  FIX: scrolling to the bottom never accumulated more options.

  Root causes in the old version:
  1) loadOptions() called setOptions([]) BEFORE every fetch, so when the
     scroll handler advanced the page, the already-loaded items were wiped
     and replaced by ONLY the new page (one page of results). The list
     looked like it never grew past the first page.
  2) handleScroll guarded on the `loading` *state*, which lags a render —
     several scroll events could fire before it flipped, jumping
     page 1 -> 4 and skipping pages 2-3 entirely.
  3) Every keystroke fired a request with no debounce and no stale-response
     guard, so an older request resolving last could clobber newer results.

  The fetch contract is unchanged:
  fetchOptions(page, searchTerm) resolves to { items: Option[], hasMore: boolean }.
*/

const CustomDropdownWithFetch = ({
  label,
  onSelect,
  placeholder = 'Select...',
  colorLabel,
  font,
  search,
  preselectedValue,
  parentClass,
  disabled,
  fetchOptions, // function to fetch options from API (page, searchTerm)
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState(
    preselectedValue ? preselectedValue.label : null,
  );

  const dropdownRef = useRef(null);
  const listRef = useRef(null);
  /* Refs (not state) guard the scroll handler — they update synchronously,
     so repeated scroll events between renders can no longer skip pages. */
  const busyRef = useRef(false);
  const seqRef = useRef(0);
  /* Always call the latest fetchOptions WITHOUT making it an effect
     dependency — an inline callback prop would otherwise re-run the effect
     on every parent render and reset pagination to page 1. */
  const fetchOptionsRef = useRef(fetchOptions);
  useEffect(() => {
    fetchOptionsRef.current = fetchOptions;
  }, [fetchOptions]);

  useEffect(() => {
    if (preselectedValue) {
      setSelectedOption(preselectedValue.label);
    }
  }, [preselectedValue]);

  /* Debounce the search box: one request per pause, not per keystroke. */
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  /* A new search term always restarts pagination from page 1. */
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  /* Fetch. Runs when the dropdown opens, the page advances (scroll), or the
     debounced search changes. Page 1 REPLACES the list; later pages APPEND.
     (The old version cleared the list before every fetch, so appended pages
     never accumulated.) */
  useEffect(() => {
    if (!isOpen) return;

    const fetchId = ++seqRef.current;
    busyRef.current = true;
    setLoading(true);

    const loadOptions = async () => {
      try {
        const data = await fetchOptionsRef.current?.(page, debouncedSearch);
        /* A newer fetch started while this one was in flight — drop this
           stale response instead of clobbering newer results. */
        if (fetchId !== seqRef.current) return;
        const items = data?.items ?? [];
        setOptions((prev) => {
          if (page === 1) return items;
          /* De-dupe in case a page ever gets requested twice */
          const seen = new Set(prev.map((option) => String(option.value)));
          return [
            ...prev,
            ...items.filter((option) => !seen.has(String(option.value))),
          ];
        });
        setHasMore(Boolean(data?.hasMore));
      } catch (err) {
        console.error('Error fetching options:', err);
      } finally {
        if (fetchId === seqRef.current) {
          busyRef.current = false;
          setLoading(false);
        }
      }
    };

    loadOptions();
  }, [isOpen, page, debouncedSearch]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /* Infinite scroll — busyRef updates synchronously, so the handler can't
     bump the page twice while a fetch is in flight (the old guard used
     `loading` state, which lags a render and let pages get skipped). */
  const handleScroll = () => {
    if (!listRef.current || busyRef.current || !hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 24) {
      busyRef.current = true; // blocked again until this fetch completes
      setPage((prev) => prev + 1);
    }
  };

  const handleSelect = (option) => {
    setSelectedOption(option.label);
    setIsOpen(false);
    onSelect(option);
  };

  const toggleOpen = (event) => {
    event.preventDefault();
    setIsOpen((prev) => {
      if (!prev) setPage(1); // opening: always start from the first page
      return !prev;
    });
  };

  return (
    <div className="relative w-full" ref={dropdownRef}>
      {label && (
        <label
          className={`block w-full mb-2 text-sm ${
            colorLabel ? colorLabel : 'text-[#464646]'
          }`}
        >
          {label}
        </label>
      )}

      {/* Button */}
      <button
        type="button"
        disabled={disabled}
        onClick={toggleOpen}
        className={`${parentClass ? parentClass : 'border-gray-300'} relative w-full border font-medium  min-h-10 rounded-md text-sm px-2 pr-10 bg-white flex items-center text-left focus:outline-none`}
      >
        <span
          className={`${
            font === 'small' ? 'text-[12px]' : 'whitespace-normal pr-2'
          } break-words`}
        >
          {selectedOption || placeholder}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-black pointer-events-none"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06-.02L10 10.69l3.71-3.5a.75.75 0 111.04 1.08l-4 3.75a.75.75 0 01-1.04 0l-4-3.75a.75.75 0 01-.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="absolute left-0 mt-2 w-full bg-white border rounded-md shadow-lg z-50"
            style={{ maxHeight: '250px' }}
          >
            {/* Search */}
            {search && (
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border-b focus:outline-none"
              />
            )}

            {/* Options list */}
            <div
              ref={listRef}
              onScroll={handleScroll}
              className="overflow-y-auto"
              style={{ maxHeight: '200px' }}
            >
              <AnimatePresence>
                {options.map((option) => (
                  <motion.div
                    key={`${option.value}-${option.label}`}
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 5 }}
                    transition={{ duration: 0.2 }}
                    onClick={() => handleSelect(option)}
                    className="px-4 py-2 hover:bg-[#375AE7] hover:text-white cursor-pointer"
                  >
                    {option.label}
                  </motion.div>
                ))}
              </AnimatePresence>

              {loading && (
                <div className="px-4 py-2 text-sm text-gray-500 animate-pulse">
                  {page === 1 ? 'Loading...' : 'Loading more...'}
                </div>
              )}

              {!loading && options.length === 0 && (
                <div className="px-4 py-2 text-gray-500">No results found</div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CustomDropdownWithFetch;
